import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, Observable, Subject } from 'rxjs';
import { ITEM_REVENUE_CATEGORY } from 'src/app/helpers/item-revenue.helper';
import { LENS_OPTIONS, LENS_OPTIONS_TYPES } from 'src/app/helpers/lens-options.helper';
import { LENS } from 'src/app/helpers/lenses.helper';
import { ORDER_CATEGORY } from 'src/app/helpers/order-categories.helper';
import { CartItems } from 'src/app/models/cart-items.model';
import { CompanyDefaultLensOptions } from 'src/app/models/company-default-lens-options.model';
import { CompanyLensOptions } from 'src/app/models/company-lens-options.model';
import { ProductDataSubject } from 'src/app/models/product-data-subject.model';
import { ItemDetailsRequest } from 'src/app/models/requests/item-details-request.model';
import { CartManagerService } from 'src/app/services/local/cart-manager.service';
import { CompanyComputerLensOptionsService } from 'src/app/services/repositories/company-computer-lens-options.service';
import { CompanyDressLensOptionsService } from 'src/app/services/repositories/company-dress-lens-options..service';
import { CompanyLensOptionsService } from 'src/app/services/repositories/company-lens-options.service';
import { CompanyRespiratorLensOptionsService } from 'src/app/services/repositories/company-respirator-lens-options.service';

@Component({
    selector: 'app-lens-options',
    templateUrl: './lens-options.component.html',
    styleUrls: ['./lens-options.component.scss'],
    standalone: false
})
export class LensOptionsComponent implements OnInit {
  toggleClass: boolean = true;
  companyLensOptions: CompanyLensOptions[] = [];
  companyDefaultLensOptions: CompanyDefaultLensOptions[] = [];
  coatingOptions: CompanyLensOptions[] = [];
  scratchOptions: CompanyLensOptions[] = [];
  tintOptions: CompanyLensOptions[] = [];
  queryParams: {} = {};
  isEditMode: boolean = false;
  hasDefaultScratch: boolean = false;
  hasDefaultCoating: boolean = false;
  hasDefaultTint: boolean = false;
  hasBifocalLens: boolean = false;
  hasBasicProgLens: boolean = false;
  ignoredDefaultOptionIds: number[] = [];
  tempCartEventsSubject: Subject<ProductDataSubject> = new Subject<ProductDataSubject>();

  private readonly lensOptionsLoaderID = 'lensOptionsLoader';

  constructor(private _route: ActivatedRoute,
              private _cartManagerService: CartManagerService,
              private _companyLensOptionsService: CompanyLensOptionsService,
              private _companyDressLensOptionsService: CompanyDressLensOptionsService,
              private _companyRespiratorLensOptionsService: CompanyRespiratorLensOptionsService,
              private _companyComputerLensOptionsService: CompanyComputerLensOptionsService,
              private _router: Router) { }

  ngOnInit(): void {
    this._route.queryParams
    .subscribe(params => {
      this.isEditMode = params.isEditMode === 'true';
      this.queryParams = this.isEditMode ? {'isEditMode' : this.isEditMode} : {};
      this.getAllLensOptions();
    });
  }

  getAllLensOptions() {
    const cartItem = this._cartManagerService.getTempCartItem();
    let lensObservable = null;
    let defaultLensObservable = null;
    if (cartItem) {
      switch (cartItem?.itemCategory) {
        case ORDER_CATEGORY.glasses:
          lensObservable = this._companyLensOptionsService.getLensOptions();
          defaultLensObservable = this._companyLensOptionsService.getDefaultLensOptions(ORDER_CATEGORY.glasses);
          break;
        case ORDER_CATEGORY.computerGlasses:
          lensObservable = this._companyComputerLensOptionsService.getLensOptions();
          defaultLensObservable = this._companyLensOptionsService.getDefaultLensOptions(ORDER_CATEGORY.computerGlasses);
          break;
        case ORDER_CATEGORY.dressGlasses:
          lensObservable = this._companyDressLensOptionsService.getLensOptions();
          defaultLensObservable = this._companyLensOptionsService.getDefaultLensOptions(ORDER_CATEGORY.dressGlasses);
          break;
        case ORDER_CATEGORY.respiratorInserts:
          lensObservable = this._companyRespiratorLensOptionsService.getLensOptions();
          defaultLensObservable = this._companyLensOptionsService.getDefaultLensOptions(ORDER_CATEGORY.respiratorInserts);
          break;
        default:
          this.redirectToDashboard();
      }

      if (lensObservable && defaultLensObservable) {
        this.populateAndSortLensOptions(lensObservable, defaultLensObservable);
      }

    }
    else {
      this.redirectToDashboard();
    }
  }

  redirectToDashboard() {
    this._router.navigate(['/dashboard']);
  }

  populateAndSortLensOptions(lensObservable: Observable<any>, defaultLensObservable: Observable<any>) {
    forkJoin({
      lensOptions: lensObservable,
      defaultLensOptions: defaultLensObservable
    })
    .subscribe(({lensOptions, defaultLensOptions}) => {
      if (lensOptions && defaultLensOptions) {
        this.companyLensOptions = lensOptions;
        this.companyDefaultLensOptions = defaultLensOptions;
        this.sortLensOptions();
        this.addDefaultLensOptions();
      }
    }, (error: any) => {
      console.log(error);
    });
  }

  addDefaultLensOptions() {
    if (this.companyDefaultLensOptions.length > 0) {
      this.companyDefaultLensOptions.forEach(defaultOption => {
        if (this.isDefaultAllowed(defaultOption)) {
          const cartItemDetail : ItemDetailsRequest = {
            id: defaultOption.lensOptionId,
            itemType: ITEM_REVENUE_CATEGORY.lensTreatment,
            itemSubType: defaultOption.category,
            itemDescription: defaultOption.lensOptionName
          };
          this._cartManagerService.updateTempCartItemDetail(cartItemDetail);
          if (defaultOption.category == LENS_OPTIONS_TYPES.scratch) {
            this.hasDefaultScratch = true;
          }
          if (defaultOption.category == LENS_OPTIONS_TYPES.coatings || defaultOption.category == LENS_OPTIONS_TYPES.ar) {
            this.hasDefaultCoating = true;
          }
          if (defaultOption.category == LENS_OPTIONS_TYPES.tints) {
            this.hasDefaultTint = true;
          }
        }
      });
      this.emitChangeToProductData();
    }
  }

  checkLenses() {
    const tempCartItem = this._cartManagerService.getTempCartItem();
    if (tempCartItem) {
      const bifocalLens = tempCartItem.itemDetails.find(details =>
        details.itemType == ITEM_REVENUE_CATEGORY.lenses &&
        details.id == LENS.bifocal);
      const basicProg = tempCartItem.itemDetails.find(details =>
        details.itemType == ITEM_REVENUE_CATEGORY.lenses &&
        details.id == LENS.basicProg);
        if (bifocalLens) {
          this.hasBifocalLens = true;
        }
        if (basicProg) {
          this.hasBasicProgLens = true;
        }
    }
  }

  isDefaultAllowed(defaultOption: CompanyDefaultLensOptions) {
    this.checkLenses();
    let isAllowed: boolean = true;

    if (this.hasBifocalLens && (
      // leave comment in in case of lab switch
      //defaultOption.lensOptionId == LENS_OPTIONS.antifog || defaultOption.lensOptionId == LENS_OPTIONS.blueLight)) {
      defaultOption.lensOptionId == LENS_OPTIONS.blueLight)) {
      isAllowed = false;
    }

    if (this.hasBasicProgLens && defaultOption.lensOptionId == LENS_OPTIONS.blueLight) {
      isAllowed = false;
    }

    if (!isAllowed) {
      if (this.ignoredDefaultOptionIds.indexOf(defaultOption.lensOptionId) == -1){
        this.ignoredDefaultOptionIds.push(defaultOption.lensOptionId);
      }
    }

    return isAllowed;
  }

  sortLensOptions() {
    this.coatingOptions = this.companyLensOptions
      .filter(cl => cl.category !== LENS_OPTIONS_TYPES.tints && cl.category !== LENS_OPTIONS_TYPES.scratch);
    this.tintOptions = this.companyLensOptions
      .filter(cl => cl.category == LENS_OPTIONS_TYPES.tints);
    this.scratchOptions = this.companyLensOptions
      .filter(cl => cl.category == LENS_OPTIONS_TYPES.scratch);
  }

  addToCart() {
    const tempCartItem = this._cartManagerService.getTempCartItem();
    if (tempCartItem) {
      if (!this.isEditMode) {
        this._cartManagerService.addCartItem(tempCartItem)
        .subscribe((newCartItem: CartItems) => {
            if (newCartItem) {
              this._cartManagerService.saveNewCartItemRef(newCartItem);
            }
            this._router.navigate(['/lens-selection/accessories']);
          },
          (error: any) => {
            console.log(error);
          });
      }
      else {
        const editItem = this._cartManagerService.getEditItem();
        if (editItem) {
          this._cartManagerService.updateCartItem(tempCartItem, editItem.itemId)
          .subscribe(() => {
            // accessories are not editable right now - LM
              this._router.navigate(['cart']);
            },
            (error: any) => {
              console.log(error);
            });
        }
      }
    }
  }

  emitChangeToProductData() {
    let pds: ProductDataSubject = { status: 'lens-options-update' };
    this.tempCartEventsSubject.next(pds);
  }
}
